/**
 * useAutoOpen — Auto-opens a form when navigating back from DocsEditor.
 *
 * DocsEditor sets location.state = { openId: elementId } when the back button
 * is clicked. Each list passes its items array and the function that opens the
 * edit form for a given item. If a matching item is found the form is opened
 * and the state is cleared so it doesn't re-trigger on subsequent renders.
 *
 * Usage:
 *   useAutoOpen(servers, handleEdit);
 *   useAutoOpen(firewalls, setSelected);
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function useAutoOpen(items, openFn) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const openFnRef = useRef(openFn);
  openFnRef.current = openFn;

  useEffect(() => {
    const rawId = location.state?.openId;
    if (!rawId || !items?.length) return;

    const targetId = Number(rawId);
    const item = items.find(i => i.id === targetId);
    if (item) {
      openFnRef.current(item);
      // Clear the state so it doesn't re-open on future visits to this route
      navigate(location.pathname + (location.search || ''), { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openId, items?.length]);
}
